import { RadioGroup } from '@/components/RadioGroup';
import {
    ELoanUsage,
    selectLoanTerm,
    selectLoanUsage,
    setBalloon,
    setLoanUsage,
} from '@/context/borrower';
import { selectPartner } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'LoanUsage';

export function LoanUsage({ baseId, showErrors }: IProps): JSX.Element {
    // ***** Redux *****
    const currentState = useAppSelector(selectLoanUsage);
    const partner = useAppSelector(selectPartner);
    const loanTerm = useAppSelector(selectLoanTerm);
    const dispatch = useAppDispatch();

    // ***** Event Handlers *****
    function handleChange(value: string | boolean | undefined): void {
        if (loanTerm > 5 && value === ELoanUsage.NOVATED) {
            dispatch(setLoanUsage(ELoanUsage.PERSONAL));
            return;
        }

        dispatch(setLoanUsage(value as ELoanUsage));

        // TODO: Move this to a separate function - this is dupliated in getQuickQuote.ts
        // Set balloon rates for novated lease
        if (value === ELoanUsage.NOVATED) {
            const balloonRates: Record<number, number> = {
                1: 65.63,
                2: 56.25,
                3: 46.88,
                4: 37.5,
                5: 28.13,
            };
            const balloonRate = balloonRates[loanTerm];
            if (balloonRate !== undefined) {
                dispatch(setBalloon(balloonRate));
            }
        } else if (partner.isNovatedLease) {
            dispatch(setBalloon(0));
        }
    }

    // ***** Build Options Array *****
    const baseOptions = [
        {
            label: 'Personal',
            value: ELoanUsage.PERSONAL,
            id: `${baseId}-button-loanUsage-personal`,
        },
        {
            label: 'Business',
            value: ELoanUsage.BUSINESS,
            id: `${baseId}-button-loanUsage-business`,
        },
    ];

    const novatedOption = {
        label: 'Novated Lease',
        value: ELoanUsage.NOVATED,
        id: `${baseId}-button-loanUsage-novated`,
    };

    // Only include novated option if partner supports it AND term is <= 5 years
    const options =
        partner.isNovatedLease && loanTerm <= 5
            ? [...baseOptions, novatedOption]
            : baseOptions;

    // ***** Render *****
    return (
        <div data-testid={testId}>
            <RadioGroup
                title="What's the purpose of the loan?"
                showErrors={showErrors}
                onChange={handleChange}
                options={options}
                value={currentState}
                key={`loan-usage-${currentState}-term-${loanTerm}`} // Force rerender when value or loan term changes
                tooltipContent='Select Business if more than 50% business use. Can also apply to employees.'
                className='leading-none '
            />
        </div>
    );
}
